import { AudioPlayerStates, ICaptionsItem } from '../../types/common';
import { IPlayerListener } from '../../components/CaptionsContainer/TargetCaptions/components/types';
import { getDispatch } from '../../store';
import { setAudioPlayerState } from '../../store/general/actions';

export interface IAudioPlayerManagerListener extends IPlayerListener {
}

type TActiveResult = {
  captionIndex: number,
  wordIndex: number
}

const audioPlayerPauseListener = () => {
  const dispatch = getDispatch();
  dispatch(setAudioPlayerState(AudioPlayerStates.PAUSED));
}

const audioPlayerPlayListener = () => {
  const dispatch = getDispatch();
  dispatch(setAudioPlayerState(AudioPlayerStates.PLAYING));
}

export class AudioPlayerManager {

  private static instance: AudioPlayerManager;

  public static getInstance(): AudioPlayerManager {
    if (!AudioPlayerManager.instance)
      AudioPlayerManager.instance = new AudioPlayerManager();
    return AudioPlayerManager.instance;
  }

  private player: HTMLMediaElement | null | undefined;
  private captions!: ICaptionsItem[];
  private activeCaptionIndex: number = -1;
  private activeWordIndex: number = 1
  private listeners: IAudioPlayerManagerListener[] = [];

  // HTMLVideoElement satisfies HTMLMediaElement, so the recording player can hand in a <video> element
  // here too — nothing below reads anything audio-specific (currentTime/play/pause/paused/ended/readyState).
  public setPlayer(player: HTMLMediaElement | null | undefined, rate: number) {
    if (this.player) {
      this.player.removeEventListener('pause', audioPlayerPauseListener)
      this.player.removeEventListener('play', audioPlayerPlayListener)
    }
    this.player = player;
    this.activeCaptionIndex = -1;
    this.activeWordIndex = -1;
    if (this.player) {
      this.player.playbackRate = rate;
      this.player.addEventListener('pause', audioPlayerPauseListener)
      this.player.addEventListener('play', audioPlayerPlayListener)
    }

    this.startWatch();
  }

  public setPlaybackRate(rate: number) {
    if (this.player) this.player.playbackRate = rate;
  }

  public setCaptions(captions: ICaptionsItem[]) {
    this.activeCaptionIndex = -1;
    this.activeWordIndex = -1
    this.captions = captions;
  }

  public getCaptions(): ICaptionsItem[] {
    return this.captions;
  }

  public addListener(listener: IAudioPlayerManagerListener) {
    this.listeners.push(listener);
  }

  public removeListener(listener: IAudioPlayerManagerListener) {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  public async play(time?: number) {
    if (!this.player) return;
    if (time !== undefined) {
      this.player.currentTime = time;
    }
    await this.player.play();
  }

  public async seek(time?: number) {
    if (!this.player) return;
    if (time !== undefined) {
      this.player.currentTime = time;
    }
  }

  public pause() {
    if (!this.player) return;
    this.player.pause();
  }

  public getCurrentTime(): number {
    return this.player?.currentTime || 0;
  }

  private startWatch() {
    setInterval(this.checkActiveIndexes.bind(this), 50);
  }

  private checkActiveIndexes() {
    if (this.player && this.captions && this.captions.length > 0)  {
      const curentTime = this.getCurrentTime();
      const {captionIndex, wordIndex} = this.getActiveResult(curentTime);
      if (captionIndex !== this.activeCaptionIndex) {
        this.activeCaptionIndex = captionIndex;
        this.listeners.forEach(l => {
          if (l.onTargetIndexChange)
            l.onTargetIndexChange(this.activeCaptionIndex);
        });
      }
      if (wordIndex !== this.activeWordIndex) {
        this.activeWordIndex = wordIndex;
        this.listeners.forEach(l => {
          if (l.onTargetWordIndexChange) {
            l.onTargetWordIndexChange(this.activeWordIndex);
          }
        });
      }
    }
  }

  public getActiveResult(currentTime: number): TActiveResult {
    for(let i=0; i<this.captions.length; i++) {
      const startTime = this.captions[i].startTime;
      let endTime = this.captions[i].endTime;
      if (i < this.captions.length - 1) {
        if (endTime > this.captions[i+1].startTime) {
          endTime = this.captions[i+1].startTime;
        }
      }
      if (currentTime >= startTime && currentTime < endTime) {
        let wordIndex = -1;
        for (let wi=0; wi<this.captions[i].words!.length; wi++) {
          const startTime = this.captions[i].words![wi].time;
          if (startTime < currentTime) {
            wordIndex = wi;
          }
        }
        return {captionIndex: i, wordIndex};
      }
    }
    return {captionIndex: -1, wordIndex: -1};
  }

  public isPlayerActive() {
    return this.player
      && this.player.currentTime > 0
      && !this.player.paused
      && !this.player.ended
      && this.player.readyState > 2;
  }
}
